import { FHIR_WHEN_TO_INDEX, getDefaultDosageRows, getUnitOptions } from './MedicationDosage.constants'

import type { MedicationCode, MedicationDosage, MedicationStatement } from '@/modules/app/types/HealthParameter.types'
import type { DosageRow } from './MedicationDosage.constants'

/**
 * Flat form values used by the inner Formik instance in
 * {@link MedicationItemForm}. Converted to/from a full
 * {@link MedicationStatement} at the form boundary.
 */
export type MedicationFormValues = {
  name: string
  dosageInstructions: string
  code?: MedicationCode | null
  dosageRows: DosageRow[]
  asNeededBoolean: boolean
}

/**
 * Returns true when dosage entries contain doseQuantity and recognized
 * FHIR timing codes. Works on raw dosage arrays so it can check both
 * current and previous dosage data.
 */
const hasDosageStructure = (dosages: MedicationDosage[]): boolean => {
  const structured = dosages.filter((d) => d.doseQuantity != null && (d.timing?.repeat?.when?.length ?? 0) > 0)
  if (structured.length === 0) return false
  return structured.every((d) => (d.timing?.repeat?.when ?? []).every((w: string) => w in FHIR_WHEN_TO_INDEX))
}

/**
 * Determines the dosage input mode for a medication entry.
 *
 * Uses the persisted `dosageMode` when available (explicit user choice),
 * otherwise infers from the current dosage data shape.
 * Does NOT fall back to `previous` — that would override a deliberate
 * free-text choice for legacy data that predates `dosageMode` persistence.
 */
export const resolveDosageMode = (entry: MedicationStatement): 'structured' | 'free' => {
  if (entry.dosageMode) return entry.dosageMode
  if (hasDosageStructure(entry.dosage ?? [])) return 'structured'
  return 'free'
}

/**
 * Whether the medication form's submit button should be disabled.
 *
 * Submitting, an invalid form, or a missing medication selection always
 * disable it. The content-presence guards only apply when adding a new
 * medication — when editing an existing entry the data already exists, so
 * the user must not be forced to re-enter dosage details to be able to save.
 *
 * When adding, structured mode requires either at least one dosage quantity
 * or the "indien nodig" (as-needed) flag — an as-needed-only medication is a
 * valid entry with no fixed daily schedule. Free mode requires non-empty text.
 */
export const isMedicationSaveDisabled = ({
  isSubmitting,
  isValid,
  hasSelectedMedication,
  isEdit,
  dosageMode,
  dosageRows,
  asNeededBoolean,
  dosageInstructions,
}: {
  isSubmitting: boolean
  isValid: boolean
  hasSelectedMedication: boolean
  isEdit: boolean
  dosageMode: 'structured' | 'free'
  dosageRows: DosageRow[]
  asNeededBoolean: boolean
  dosageInstructions: string | undefined
}): boolean => {
  if (isSubmitting || !isValid || !hasSelectedMedication) return true
  if (isEdit) return false
  if (dosageMode === 'structured') return !asNeededBoolean && dosageRows.every((r) => r.quantity === 0)
  return !dosageInstructions?.trim()
}

/**
 * Reverse-map {@link MedicationDosage}[] back into {@link DosageRow}[]
 * for the structured input. Starts from the default rows and overwrites
 * quantity/unit for matching FHIR `when` codes.
 */
const resolveUnit = (unit: string | null | undefined): string => {
  if (!unit) return 'pills'
  return getUnitOptions().some((o) => o.value === unit) ? unit : 'pills'
}

const dosageToDosageRows = (dosages: MedicationDosage[]): DosageRow[] => {
  const rows = getDefaultDosageRows()
  const structured = dosages.filter((d) => d.doseQuantity != null && d.timing?.repeat?.when?.[0])
  const sharedUnit = resolveUnit(structured[0]?.doseQuantity?.unit)

  for (const d of structured) {
    for (const when of d.timing?.repeat?.when ?? []) {
      const index = FHIR_WHEN_TO_INDEX[when]
      if (index == null) continue
      rows[index] = {
        ...rows[index],
        quantity: d.doseQuantity!.value,
        unit: sharedUnit,
      }
    }
  }

  return rows.map((row) => ({ ...row, unit: sharedUnit }))
}

/**
 * Find the best available structured dosage source: current data first,
 * then previous (synops) data, then null if neither has structure.
 */
const findStructuredDosages = (entry: MedicationStatement): MedicationDosage[] | null => {
  if (hasDosageStructure(entry.dosage ?? [])) return entry.dosage
  if (entry.previous && hasDosageStructure(entry.previous.dosage ?? [])) return entry.previous.dosage
  return null
}

/**
 * Convert a persisted {@link MedicationStatement} into flat
 * {@link MedicationFormValues} for the add/edit form.
 *
 * Populates `dosageRows` from the best available structured source
 * (current → previous → defaults) so the structured input is always
 * pre-filled when structured data exists anywhere on the entry.
 */
export const medicationStatementToFormValues = (entry: MedicationStatement): MedicationFormValues => {
  const structuredDosages = findStructuredDosages(entry)
  return {
    name: entry.name,
    dosageInstructions: entry.dosage?.[0]?.text ?? '',
    code: entry.code ?? null,
    dosageRows: structuredDosages ? dosageToDosageRows(structuredDosages) : getDefaultDosageRows(),
    asNeededBoolean: entry.dosage?.some((d) => d.asNeededBoolean === true) ?? false,
  }
}

const getUnitLabel = (unitValue: string): string =>
  getUnitOptions().find((o) => o.value === unitValue)?.label ??
  getUnitOptions().find((o) => o.value === 'pills')?.label ??
  unitValue

/**
 * Convert flat {@link MedicationFormValues} back into a full
 * {@link MedicationStatement} suitable for Formik / API submission.
 *
 * Dosage mapping depends on `dosageMode`:
 * - `'structured'` — each non-zero {@link DosageRow} becomes a
 *   {@link MedicationDosage} with `timing.repeat.when` (FHIR code),
 *   `doseQuantity` (value + unit), and a human-readable `text`. When no row
 *   has a quantity but `asNeededBoolean` is set, a single as-needed-only
 *   carrier entry is stored instead (see {@link dosageRowsToDosage}).
 * - `'free'` — the raw `dosageInstructions` string is stored as
 *   `dosage[0].text` with default timing (no quantity or timing codes).
 *
 * Always creates an `inserted` statement with `source: 'patient'`.
 */
export const formValuesToMedicationStatement = (
  values: MedicationFormValues,
  dosageMode: 'structured' | 'free',
): MedicationStatement => ({
  synopsId: null,
  diffStatus: 'inserted',
  name: values.name,
  code: values.code ?? null,
  source: 'patient',
  status: 'recorded',
  updatedAt: new Date().toISOString(),
  dosageMode,
  dosage:
    dosageMode === 'structured'
      ? dosageRowsToDosage(values.dosageRows, values.asNeededBoolean)
      : freeTextToDosage(values.dosageInstructions),
})

const dosageRowsToDosage = (rows: DosageRow[], asNeededBoolean: boolean) => {
  const quantityDosages = rows
    .filter((r) => r.quantity > 0)
    .map((row) => ({
      text: `${row.quantity} ${getUnitLabel(row.unit)} ${row.timeOfDay}`,
      timing: {
        repeat: {
          frequency: 1,
          period: 1,
          periodUnit: 'd',
          when: [row.fhirWhen],
        },
      },
      doseQuantity: { value: row.quantity, unit: row.unit },
      ...(asNeededBoolean && { asNeededBoolean: true }),
    }))

  // As-needed-only entry: the patient ticked "indien nodig" without a fixed
  // daily schedule. Emit a single carrier entry so the flag survives the
  // round-trip; the displayed label is derived from `asNeededBoolean` in
  // getMedicationDosageText, so the text is intentionally left empty here.
  if (quantityDosages.length === 0 && asNeededBoolean) {
    return [{ text: '', timing: null, asNeededBoolean: true }]
  }

  return quantityDosages
}

const freeTextToDosage = (text?: string) =>
  text
    ? [
        {
          text,
          timing: null,
        },
      ]
    : []
